import { useState } from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { X } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
    EQUIPOS,
    CONSUMIBLES,
    money0,
    unit,
    equipoVenta,
    equipoOtrosDetalle,
    equipoLista,
    consUnitBase,
    eqWithOverrides,
    consWithOverrides,
} from "@/lib/unidosis";
import { AlertTriangle } from "lucide-react";

const PARAM_ROWS = [
    { key: "logEq", label: "Costo logístico equipos", pct: true },
    { key: "arancelEq", label: "Aranceles equipos", pct: true },
    { key: "margenEq", label: "Margen equipos", pct: true },
    { key: "logCo", label: "Costo logístico consumibles", pct: true },
    { key: "arancelCo", label: "Aranceles consumibles", pct: true },
    { key: "margenCo", label: "Margen consumibles", pct: true },
    { section: "Otros costos adicionales (equipos)" },
    { key: "otrosImpl", label: "Implementación (% del costo)", pct: true },
    { key: "otrosMant", label: "Mantenimiento (% del costo)", pct: true },
    { key: "otrosImprev", label: "Imprevistos (% del costo+log)", pct: true },
    { key: "otrosGastos", label: "Gastos operativos (% de venta)", pct: true },
    { section: "Financiamiento e impuestos" },
    { key: "tasaFin", label: "Tasa financiamiento (anual)", pct: true },
    { key: "tasaAlq", label: "Tasa alquiler (anual)", pct: true },
    { key: "iva", label: "IVA privado", pct: true },
];

const PLAZO_LABEL = {
    1: "1 año",
    2: "2 años",
    3: "3 años",
    4: "4 años (multi)",
};

export default function SettingsDialog({ open, onOpenChange, state, setState }) {
    const [tab, setTab] = useState("params");

    if (!state) return null;

    const setParam = (k, v) => {
        setState({
            ...state,
            settings: {
                ...state.settings,
                params: { ...state.settings.params, [k]: v },
            },
        });
    };
    const setTier = (i, patch) => {
        const tiers = state.settings.tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t));
        setState({ ...state, settings: { ...state.settings, tiers } });
    };
    const setPlazoDesc = (k, v) => {
        setState({
            ...state,
            settings: {
                ...state.settings,
                plazo: { ...state.settings.plazo, [k]: v },
            },
        });
    };
    const setEqOverride = (id, patch) => {
        const cur = (state.eqOverrides || {})[id] || {};
        setState({
            ...state,
            eqOverrides: { ...(state.eqOverrides || {}), [id]: { ...cur, ...patch } },
        });
    };
    const setConsOverride = (id, patch) => {
        const cur = (state.consOverrides || {})[id] || {};
        setState({
            ...state,
            consOverrides: { ...(state.consOverrides || {}), [id]: { ...cur, ...patch } },
        });
    };

    return (
        <SheetPrimitive.Root open={open} onOpenChange={onOpenChange} modal={false}>
            <SheetPrimitive.Portal>
                {/* No dark overlay — keep the quote panel fully visible/interactive. A soft edge indicator is provided via the shadow on the sheet itself. */}
                <SheetPrimitive.Content
                    data-testid="settings-dialog"
                    onInteractOutside={(e) => e.preventDefault()}
                    onPointerDownOutside={(e) => e.preventDefault()}
                    className="fixed inset-y-0 left-0 z-50 h-full w-full sm:max-w-[560px] bg-white shadow-2xl border-r border-slate-200 flex flex-col transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-400 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left focus:outline-none"
                >
                    <SheetPrimitive.Close
                        data-testid="settings-close"
                        className="absolute right-4 top-4 rounded-md h-8 w-8 grid place-items-center border border-slate-200 hover:bg-slate-100 transition-colors"
                    >
                        <X className="h-4 w-4 text-slate-600" />
                        <span className="sr-only">Cerrar</span>
                    </SheetPrimitive.Close>

                    <div className="px-6 pt-6 pb-3 border-b border-slate-200">
                        <SheetPrimitive.Title className="font-heading tracking-tight text-lg font-semibold text-slate-900">
                            Precios y reglas de negocio
                        </SheetPrimitive.Title>
                        <SheetPrimitive.Description className="text-[13px] text-slate-500 mt-1 leading-relaxed pr-10">
                            Estos valores se calculan a partir del modelo (costo → logística → aranceles → margen).
                            Los cambios se aplican en vivo — el panel de cotización a la derecha se actualiza mientras editás.
                        </SheetPrimitive.Description>
                    </div>

                    <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0 px-6 pt-4">
                        <TabsList className="mb-3 self-start">
                            <TabsTrigger value="params" data-testid="settings-tab-params">Parámetros</TabsTrigger>
                            <TabsTrigger value="equipos" data-testid="settings-tab-equipos">Equipos</TabsTrigger>
                            <TabsTrigger value="consumibles" data-testid="settings-tab-consumibles">Consumibles</TabsTrigger>
                            <TabsTrigger value="tiers" data-testid="settings-tab-tiers">Tiers de volumen</TabsTrigger>
                        </TabsList>

                        <div className="flex-1 overflow-y-auto thin-scroll pr-1 pb-6">
                            <TabsContent value="params" className="mt-0">
                                <table className="w-full text-[12.5px]">
                                <thead>
                                    <tr className="text-slate-500">
                                        <th className="text-left py-2 border-b border-slate-200 font-medium text-[11px] uppercase tracking-widest">Parámetro</th>
                                        <th className="text-right py-2 border-b border-slate-200 font-medium text-[11px] uppercase tracking-widest">Valor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {PARAM_ROWS.map((r, i) =>
                                        r.section ? (
                                            <tr key={"s-" + i}>
                                                <td colSpan={2} className="pt-3 pb-1 font-heading font-semibold text-slate-800 border-b border-slate-200">
                                                    {r.section}
                                                </td>
                                            </tr>
                                        ) : (
                                            <tr key={r.key} className="border-b border-dashed border-slate-100">
                                                <td className="py-1.5">{r.label}</td>
                                                <td className="py-1.5 text-right">
                                                    <div className="inline-flex items-center gap-1">
                                                        <NumInput
                                                            testId={`param-${r.key}`}
                                                            value={(state.settings.params[r.key] * 100).toFixed(2)}
                                                            onChange={(v) => setParam(r.key, (+v || 0) / 100)}
                                                        />
                                                        <span className="text-slate-400 text-xs">%</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                        </TabsContent>

                        <TabsContent value="equipos" className="mt-0">
                            <table className="w-full text-[12px]">
                                <thead className="text-[11px] uppercase tracking-widest text-slate-500">
                                    <tr>
                                        <th className="text-left py-2 border-b border-slate-200 font-medium">Equipo</th>
                                        <th className="text-right py-2 border-b border-slate-200 font-medium">Costo 2026</th>
                                        <th className="text-right py-2 border-b border-slate-200 font-medium">P. venta</th>
                                        <th className="text-right py-2 border-b border-slate-200 font-medium">Otros fijos</th>
                                        <th className="text-right py-2 border-b border-slate-200 font-medium">Otros costos</th>
                                        <th className="text-right py-2 border-b border-slate-200 font-medium">Monto total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {EQUIPOS.map((eRaw) => {
                                        const e = eqWithOverrides(state, eRaw);
                                        const ot = equipoOtrosDetalle(state, e);
                                        return (
                                            <tr key={e.id} className="border-b border-dashed border-slate-100">
                                                <td className="py-1.5 pr-2">{e.nombre}</td>
                                                <td className="py-1.5 text-right">
                                                    <NumInput
                                                        testId={`eq-cost-${e.id}`}
                                                        value={e.cost}
                                                        onChange={(v) => setEqOverride(e.id, { cost: +v || 0, listaManual: null })}
                                                    />
                                                </td>
                                                <td className="py-1.5 text-right font-mono text-slate-500">{money0(equipoVenta(state, e))}</td>
                                                <td className="py-1.5 text-right">
                                                    <NumInput
                                                        testId={`eq-fijos-${e.id}`}
                                                        value={e.otrosFijos || 0}
                                                        onChange={(v) => setEqOverride(e.id, { otrosFijos: +v || 0, listaManual: null })}
                                                    />
                                                </td>
                                                <td className="py-1.5 text-right font-mono text-slate-500">{money0(ot.total)}</td>
                                                <td className="py-1.5 text-right">
                                                    <NumInput
                                                        testId={`eq-lista-${e.id}`}
                                                        value={equipoLista(state, e).toFixed(0)}
                                                        strong
                                                        onChange={(v) => setEqOverride(e.id, { listaManual: +v || 0 })}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            <div className="text-[11.5px] text-slate-500 mt-3 leading-relaxed">
                                <b>Monto total</b> = precio venta + otros costos. Es el precio final usado en contado, financiamiento, alquiler y comodato.
                            </div>
                        </TabsContent>

                        <TabsContent value="consumibles" className="mt-0">
                            <table className="w-full text-[12px]">
                                <thead className="text-[11px] uppercase tracking-widest text-slate-500">
                                    <tr>
                                        <th className="text-left py-2 border-b border-slate-200 font-medium">Consumible</th>
                                        <th className="text-right py-2 border-b border-slate-200 font-medium">Costo/rollo</th>
                                        <th className="text-right py-2 border-b border-slate-200 font-medium">MOQ (u)</th>
                                        <th className="text-right py-2 border-b border-slate-200 font-medium">Unit. venta</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.keys(CONSUMIBLES).flatMap((fam) => [
                                        <tr key={"h-" + fam}>
                                            <td colSpan={4} className="pt-3 pb-1 font-heading font-semibold text-slate-800 bg-slate-50 border-b border-slate-200 px-1">
                                                {CONSUMIBLES[fam].label}
                                            </td>
                                        </tr>,
                                        ...CONSUMIBLES[fam].items.map((cRaw) => {
                                            const c = consWithOverrides(state, cRaw);
                                            return (
                                                <tr key={c.id} className="border-b border-dashed border-slate-100">
                                                    <td className="py-1.5 pr-2">{c.nombre}</td>
                                                    <td className="py-1.5 text-right">
                                                        <NumInput
                                                            testId={`cons-cost-${c.id}`}
                                                            value={c.cost}
                                                            onChange={(v) => setConsOverride(c.id, { cost: +v || 0 })}
                                                        />
                                                    </td>
                                                    <td className="py-1.5 text-right">
                                                        <NumInput
                                                            testId={`cons-moq-${c.id}`}
                                                            value={c.moq}
                                                            onChange={(v) => setConsOverride(c.id, { moq: +v || 1 })}
                                                        />
                                                    </td>
                                                    <td className="py-1.5 text-right font-mono font-semibold">
                                                        {unit(consUnitBase(state, c))}
                                                    </td>
                                                </tr>
                                            );
                                        }),
                                    ])}
                                </tbody>
                            </table>
                        </TabsContent>

                        <TabsContent value="tiers" className="mt-0">
                            <div className="flex items-start gap-2 text-[12px] p-3 rounded-md bg-blue-50 text-[#1BA5D9] mb-4">
                                <AlertTriangle className="h-4 w-4 mt-0.5 flex-none" />
                                <div>El descuento por volumen se activa cuando el consumo anual supera un múltiplo del MOQ de referencia.</div>
                            </div>
                            <table className="w-full text-[12.5px]">
                                <thead className="text-[11px] uppercase tracking-widest text-slate-500">
                                    <tr>
                                        <th className="text-left py-2 border-b border-slate-200 font-medium">Nivel</th>
                                        <th className="text-right py-2 border-b border-slate-200 font-medium">Múltiplo MOQ ≥</th>
                                        <th className="text-right py-2 border-b border-slate-200 font-medium">Descuento</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {state.settings.tiers.map((t, i) => (
                                        <tr key={i} className="border-b border-dashed border-slate-100">
                                            <td className="py-2">{i + 1}</td>
                                            <td className="py-2 text-right">
                                                <NumInput testId={`tier-mult-${i}`} step="0.5" value={t.mult} onChange={(v) => setTier(i, { mult: +v || 1 })} />
                                            </td>
                                            <td className="py-2 text-right">
                                                <div className="inline-flex items-center gap-1">
                                                    <NumInput testId={`tier-desc-${i}`} value={(t.desc * 100).toFixed(1)} onChange={(v) => setTier(i, { desc: (+v || 0) / 100 })} />
                                                    <span className="text-slate-400 text-xs">%</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <div className="mt-6 text-[12px] p-3 rounded-md bg-slate-50 border border-slate-200 mb-3">
                                Descuento adicional por plazo del contrato de consumibles:
                            </div>
                            <table className="w-full text-[12.5px]">
                                <thead className="text-[11px] uppercase tracking-widest text-slate-500">
                                    <tr>
                                        <th className="text-left py-2 border-b border-slate-200 font-medium">Plazo</th>
                                        <th className="text-right py-2 border-b border-slate-200 font-medium">Descuento</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.keys(PLAZO_LABEL).map((k) => (
                                        <tr key={k} className="border-b border-dashed border-slate-100">
                                            <td className="py-2">{PLAZO_LABEL[k]}</td>
                                            <td className="py-2 text-right">
                                                <div className="inline-flex items-center gap-1">
                                                    <NumInput
                                                        testId={`plazo-desc-${k}`}
                                                        value={((state.settings.plazo[k] || 0) * 100).toFixed(1)}
                                                        onChange={(v) => setPlazoDesc(k, (+v || 0) / 100)}
                                                    />
                                                    <span className="text-slate-400 text-xs">%</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </TabsContent>
                        </div>
                    </Tabs>
                </SheetPrimitive.Content>
            </SheetPrimitive.Portal>
        </SheetPrimitive.Root>
    );
}

function NumInput({ value, onChange, step, strong, testId }) {
    return (
        <Input
            type="number"
            step={step || "any"}
            value={value}
            data-testid={testId}
            onChange={(e) => onChange(e.target.value)}
            className={
                "w-28 h-8 rounded-md text-right font-mono " +
                (strong ? "font-bold" : "") +
                " focus-visible:ring-[#1BA5D9] inline-block ml-auto"
            }
        />
    );
}
